import { useMutation } from '@apollo/client';
import {
  ContentDeciderContainer,
  getTranslatedText,
  toast,
  TableContainer,
  SkeletonTableLoading
} from '@nile/components';
import { AccountClient } from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import ReportsContext from '@nile/reports/src/modules/home/contexts/Reports';
import TableView from '@nile/banyan/src/Pages/ServiceCatalog/TableView';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import { DEFAULT_VIEW } from '@nile/reports/src/modules/home/constants';
import {
  DELETE_CHARTS,
  GET_ALL_CHARTS
} from '@nile/reports/src/modules/charts/graphql';
import {
  checkDynamicQuery,
  getAccessor,
  getDefaultValue,
  getType,
  isEditable,
  isFilterable,
  isSortable,
  updateConditions,
  getAsyncOptions,
  fetchValues,
  generateOptions
} from '@nile/reports/src/modules/charts/utils/list-helpers';
import LinkCell from '@nile/reports/src/modules/charts/utils/list-helpers/LinkCell';
import CreatedByCell from '@nile/reports/src/modules/charts/utils/list-helpers/CreatedByCell';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { isEmpty } from 'lodash';
import ChartHome from '../ChartHome';

const ROW_COUNT = 20;

function ChartList(props) {
  const { activeView, activeViewLoading, viewType } = props;
  const intl = useIntl();

  const loading = false;
  const reportsContext = useContext(ReportsContext);
  const tabContext = useContext(TabContext);
  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', columns: [] }
  ]);
  const [data, setData] = useState([]);
  const [records, setRecords] = useState([]);

  const [openChart, setOpenChart] = useState(null);
  const [selectedChart, setSelectedChart] = useState({ data: null, index: 0, originalData: null });

  const { chartFilter, setChartFilter } = reportsContext;

  const activeFilter =
    (chartFilter &&
      chartFilter[viewType] &&
      chartFilter[viewType][activeView.id]) ||
    null;
  /**
   * Default options for the list info to use in table
   */
  const options = {
    pageSize: ROW_COUNT,
    page: 1,
    sort: [
      {
        attribute: 'createdTime',
        order: 'DESC'
      }
    ]
  };
  const [listLoading, setListLoading] = useState(true);
  const [listInfo, setListInfo] = useState({});

  const [deleteChart] = useMutation(DELETE_CHARTS, {
    client: AccountClient,
    onCompleted(res) {
      if (res?.deleteCharts) {
        toast('success', 'reports.widget.deleteWidget.success');
      }
    }
  });

  const checkDeleteItemAllowPermission = (item) => {
    // disable delete for default widget
    return !item.systemChart;
  };

  const getActions = (col) => {
    switch (col.name) {
      case 'name':
        return {
          onClick: (item) => {
            setOpenChart(item);
          }
        };
      case 'delete':
        return {
          onClick: deleteItem,
          checkDeleteItemAllowPermission: checkDeleteItemAllowPermission
        };
      default:
        return {};
    }
  };

  const sectionedChartFilter = (data) => {
    const filter = {
      ...chartFilter,
      [viewType]: {
        ...(chartFilter && chartFilter[viewType]),
        [activeView.id]: data
      }
    };
    setChartFilter(filter);
  };

  const updateAllConditions = (condition, selectedField) => {
    updateConditions({
      condition,
      selectedField,
      activeFilter,
      setConditions: sectionedChartFilter
    });
  };
  /**
   * Method to get Charts.
   * Call this method to fetch/refetch the data on mounting/sorting/filtering
   * @param {object} personalization
   * @param {object} pageOptions
   */
  const fetchViewData = async (personalization, pageOptions) => {
    if (!activeView?.id) {
      return;
    }
    setListLoading(true);
    let requestOption = pageOptions
      ? {
          ...pageOptions,
          view: { id: activeView?.id },
          sort: pageOptions?.sort || options?.sort,
          condition: pageOptions?.condition || activeFilter
        }
      : {
          ...options,
          condition: activeFilter,
          view: { id: activeView.id },
          sort: listInfo?.sort || options?.sort
        };
    requestOption = {
      ...requestOption,
      condition: !!activeFilter
        ? {
            ...activeFilter,
            operands: activeFilter.operands.concat({
              attribute: 'systemChart',
              operator: 'is',
              value: reportsContext.activeView === DEFAULT_VIEW
            })
          }
        : {
            joinOperator: 'AND',
            operands: [
              {
                attribute: 'systemChart',
                operator: 'is',
                value: reportsContext.activeView === DEFAULT_VIEW
              }
            ]
          }
    };
    const response = await AccountClient.query({
      query: GET_ALL_CHARTS,
      variables: {
        listInfo: requestOption
      },

      fetchPolicy: 'network-only'
    });

    setListLoading(false);
    const cols = [
      {
        name: 'name',
        label: getTranslatedText(intl, 'reports.widget.list.name'),
        dataType: 'string',
        uiDataType: 'text',
        filterType: 'text'
      },
      {
        name: 'description',
        label: getTranslatedText(intl, 'reports.widget.list.description')
      },
      {
        name: 'createdBy.name',
        label: getTranslatedText(intl, 'reports.chart.list.createdBy'),
        dataType: 'string',
        uiDataType: 'select',
        filterType: 'select',
        autoComplete: true
      }
    ];
    const columns = cols.map((col, idx) => {
      return {
        Header: col.label,
        accessor: getAccessor(col),
        type: getType(col),
        id: col.name,
        disableResizing: false,
        className: 'sticky',
        disableSortBy: !isSortable(col),
        editable: isEditable(col),
        disableFilters: !isFilterable(col),
        helpers: {
          getDefaultValue,
          updateConditions: updateAllConditions,
          checkDynamicQuery,
          getAsyncOptions,
          fetchValues,
          generateOptions
        },
        actions: getActions(col),
        field: col
      };
      // columnGroup[0].columns.push(column);
    });
    if (reportsContext.activeView !== DEFAULT_VIEW) {
      columns.push({
        Header: ' ',
        accessor: 'roundUpValue',
        width: 14,
        id: 'delete',
        pinned: 'right',
        type: 'Action',
        action: true,
        disableSortBy: true,
        disableFilters: true,
        actions: getActions({ name: 'delete' }),
        field: { name: 'delete' }
      });
    }
    setColumnGroup([{ Header: 'Name', columns }]);
    setData(response?.data?.getCharts?.charts);
    setListInfo(response?.data?.getCharts?.listInfo);
  };

  const context = {
    setRecords: setRecords,
    viewConditions: activeFilter
  };

  const defaultColumnCells = {
    Link: LinkCell,
    CreatedBy: CreatedByCell
  };

  useEffect(() => {
    if (!isEmpty(activeFilter))
      fetchViewData(null, {
        pageSize: ROW_COUNT,
        page: 1,
        view: { id: activeView.id },
        condition: activeFilter || null
      });
  }, [JSON.stringify(activeFilter)]);

  useEffect(() => {
    fetchViewData(null, {
      view: { id: activeView.id },
      pageSize: ROW_COUNT,
      page: 1
    });
  }, [activeView]);

  useEffect(() => {
    if (tabContext?.activeTab?.state?.widget)
      setOpenChart(tabContext?.activeTab?.state?.widget);
  }, [tabContext?.activeTab?.state?.widget]);

  const refetch = async (props) => {
    await fetchViewData(null, {
      page: props?.resetPage ? 1 : listInfo?.page,
      pageSize: ROW_COUNT,
      view: { id: activeView.id }
    });
    reportsContext?.setChartListMeta({
      refresh: false,
      data: null
    });
  };

  useEffect(() => {
    if (props.meta.refresh) refetch(props.meta.data);
  }, [JSON.stringify(props.meta)]);

  /**
   * Delete single row item and refresh table
   * @param {object} meta
   */
  const deleteItem = (meta) => {
    deleteChart({
      variables: {
        chartIds: [meta.chartId]
      }
    }).then(() => {
      fetchViewData(null, {
        page: 1,
        pageSize: 20,
        sort: [
          {
            attribute: 'createdTime',
            order: 'DESC'
          }
        ],
        view: { id: activeView.id }
      });
    });
  };

  useEffect(() => {
    if (records.length) {
      reportsContext?.setShowBulkMenu(true);
      reportsContext?.setSelectedChart(records);
    } else {
      reportsContext?.setShowBulkMenu(false);
      reportsContext?.setSelectedChart([]);
    }
  }, [records]);

  /**
   * Method to check if data table is visible
   * @param {*} chart 
   * @returns boolean
   */
  const isDataTableHidden = (chart) => {
    return (chart && !chart.chartId && chart.entity === 'AssetSoftwareInventory');
  };

  if (loading || activeViewLoading) {
    return <SkeletonTableLoading />;
  }

  return (
    <>
      <TableContainer>
        <ContentDeciderContainer
          isLoading={loading || !columnGroup[0]?.columns?.length}
          hasContent
          mainContent={
            <TableView
              context={context}
              columns={columnGroup}
              data={data}
              setData={setData}
              rowCount={ROW_COUNT}
              refreshTable={fetchViewData}
              listInfo={listInfo}
              pagination={
                Math.ceil(listInfo?.totalCount / listInfo?.pageSize) > 1
              }
              pages={Math.ceil(listInfo?.totalCount / listInfo?.pageSize) || 0}
              recordCount={listInfo?.totalCount}
              defaultColumnCells={defaultColumnCells}
              activePage={listInfo?.page || 0}
              isLoading={listLoading}
              fullHeight
              hideBorderTop
              noBulkSelect={reportsContext?.activeView === DEFAULT_VIEW}
            />
          }
          fullHeight
          loadingContent={<SkeletonTableLoading />}
          notFoundURL={`${envConfig.imageRoot}svg/not-found.svg`}
        />
      </TableContainer>
      {!isEmpty(openChart) && (
        <ChartHome
          mode="edit"
          callBack={() => {
            setOpenChart(false);
          }}
          chart={openChart}
        />
      )}
    </>
  );
}

export default withRouter(ChartList);
